import Icon from "@components/Atoms/Icon";
import {
	ProviderPickerItem,
	ProviderPickerLoading,
} from "@ext/articleProvider/components/ProviderPicker/ProviderPickerItem";
import t from "@ext/localization/locale/translate";
import type { TemplatePicker } from "@ext/templates/components/TemplatePicker/useTemplatePicker";
import { TEMPLATES_PANEL_ID } from "@ext/templates/components/TemplatesPanel/types/constants";
import { WorkspaceToolsManage, WorkspaceToolsPicker } from "@ext/workspaceTools/components"; // workspace-tools seam
import { DropdownEmpty, DropdownMenuItem, DropdownMenuSearchItem, DropdownMenuSeparator } from "@ui-kit/Dropdown";
import { usePanelToggle } from "@ui-kit/FloatingPanel";
import { useRef } from "react";

const CatalogTemplates = ({ picker }: { picker: TemplatePicker }) => {
	const { templates, filteredTemplates, isReady, applyTemplate } = picker;

	if (!isReady) return <ProviderPickerLoading />;
	if (!templates.length) return <DropdownMenuItem disabled>{t("template.no-templates")}</DropdownMenuItem>;
	if (!filteredTemplates.length) return <DropdownEmpty>{t("list.no-results-found")}</DropdownEmpty>;

	return filteredTemplates.map((item) => (
		<ProviderPickerItem
			key={item.id}
			onSelect={() => void applyTemplate(item)}
			textValue={item.title ?? ""}
			title={item.title || t("article.no-name")}
		/>
	));
};

const ManageTemplatesItem = () => {
	const triggerRef = useRef<HTMLDivElement>(null);
	const { toggle } = usePanelToggle(TEMPLATES_PANEL_ID, triggerRef);

	return (
		// workspace-tools seam
		<WorkspaceToolsManage
			catalogItem={(tools) => (
				<DropdownMenuItem aria-describedby={tools?.describedBy} onSelect={toggle} textValue="manage-templates">
					<div
						className="flex w-full min-w-0 items-center gap-2"
						data-testid="manage-templates"
						ref={triggerRef}
					>
						<Icon code="layout-template" />
						{t("templates-panel-manage")}
						{tools?.indicator}
					</div>
				</DropdownMenuItem>
			)}
		/>
	);
};

export const TemplatePickerItems = ({ picker }: { picker: TemplatePicker }) => {
	const { search, setSearch, inputRef, handleInputKeyDown, templates, filteredTemplates, isReady, applyTemplate } =
		picker;
	const hasTemplates = templates.length > 0;

	return (
		<>
			<DropdownMenuSearchItem
				onChange={(event) => setSearch(event.target.value)}
				onClick={(event) => event.stopPropagation()}
				onKeyDown={handleInputKeyDown}
				placeholder={`${t("find2")} ${t("template.name").toLowerCase()}`}
				ref={inputRef}
				value={search}
			/>
			<DropdownMenuSeparator />
			<WorkspaceToolsPicker
				catalogEmpty={isReady && !hasTemplates}
				catalogItems={<CatalogTemplates picker={picker} />}
				catalogNotFound={isReady && hasTemplates && filteredTemplates.length === 0}
				onSelect={(ref, title) => void applyTemplate({ id: ref.id, title })}
				query={search}
				type="template"
			/>
			<div className="mt-auto">
				<DropdownMenuSeparator />
				<ManageTemplatesItem />
			</div>
		</>
	);
};
